// API para gestión de personas (profiles) y suscripciones (clientes).
// Modelo: una persona puede tener N suscripciones; cada una con titular y opcional beneficiario.

import { supabase } from './supabase'
import { geocodificar } from './geo'

/* ─────────────────────── PERSONAS ─────────────────────── */

/** Busca personas por nombre/teléfono/email/documento para autocompletar. */
export async function buscarPersonas(query, limit = 8) {
  if (!query || query.trim().length < 2) return []
  const q = query.trim()
  // Buscamos en profiles. Como Supabase no soporta OR sobre todo en una sola query
  // amistosa, hacemos un OR con varios campos.
  const { data, error } = await supabase
    .from('profiles')
    .select('id, nombre, phone, email, documento, documento_tipo, role')
    .or(`nombre.ilike.%${q}%,phone.ilike.%${q}%,email.ilike.%${q}%,documento.ilike.%${q}%`)
    .limit(limit)
  if (error) { console.error('buscarPersonas:', error); return [] }
  return data || []
}

/** Busca persona por documento exacto (para detectar duplicado al cargar nueva). */
export async function buscarPorDocumento(tipo, documento) {
  if (!tipo || !documento?.trim()) return null
  const { data, error } = await supabase
    .from('profiles')
    .select('id, nombre, phone, email, documento, documento_tipo, role')
    .eq('documento_tipo', tipo)
    .eq('documento', documento.trim())
    .maybeSingle()
  if (error) { console.error('buscarPorDocumento:', error); return null }
  return data
}

/** Crea una persona nueva (profile sin auth user → solo referencia). */
export async function crearPersona({
  nombre, phone = null, email = null,
  documento = null, documentoTipo = null,
}) {
  const { data, error } = await supabase.rpc('crear_persona_referencia', {
    nombre_param: (nombre || '').trim(),
    phone_param: phone?.trim() || null,
    email_param: email?.trim()?.toLowerCase() || null,
    documento_param: documento?.toString().trim() || null,
    documento_tipo_param: documentoTipo || null,
    role_param: 'cliente',
  })
  if (error) {
    // Mensajes amigables para errores de unique constraint
    const msg = String(error.message || '')
    if (msg.includes('profiles_email_unq') || msg.includes('email')) {
      throw new Error(`Ya existe alguien con ese email (${email}). Buscalo en la lista en lugar de crear duplicado.`)
    }
    if (msg.includes('profiles_documento_unq') || msg.includes('documento')) {
      throw new Error(`Ya existe alguien con ese documento. Buscalo en la lista en lugar de crear duplicado.`)
    }
    throw error
  }
  return data?.[0] || null
}

/* ─────────────────────── SUSCRIPCIONES ─────────────────────── */

/**
 * Crea una suscripción. Si beneficiarioId es null, el titular recibe.
 * Geocodea la dirección automáticamente con OpenStreetMap.
 */
export async function crearSuscripcion({
  titularId, beneficiarioId = null, alias = null,
  barrioId, barrioNombre,  // barrio_id (FK) + nombre como cache
  direccion, apto = null, tipoVivienda = null,
  kitCodigo = null, diaEntrega = null, ventanaHoraria = 'manana',
  semanaMes = null, diaCobroMes = null,
  modeloCobro = 'prepaid_mensiversario',
  porteroReferidoId = null, notas = null,
  coordsManual = null,  // si viene { lat, lng }, lo usa directo (pin verificado en mapa)
}) {
  // 1. Coords: prioridad al pin manual; si no, geocodificar
  let lat = coordsManual?.lat ?? null
  let lng = coordsManual?.lng ?? null
  if (!lat && direccion && barrioNombre) {
    const geo = await geocodificar(`${direccion}, ${barrioNombre}`)
    if (geo) { lat = geo.lat; lng = geo.lng }
  }
  // 2. Insertar
  const { data, error } = await supabase
    .from('clientes')
    .insert({
      titular_id: titularId,
      beneficiario_id: beneficiarioId || null,
      alias: alias?.trim() || null,
      barrio_id: barrioId,
      barrio: (barrioNombre || '').trim(),  // cache del nombre para queries rápidas
      direccion: (direccion || '').trim(),
      apto: apto?.trim() || null,
      tipo_vivienda: tipoVivienda || null,
      lat, lng,
      geocoded_at: lat ? new Date().toISOString() : null,
      kit_actual: kitCodigo || null,
      dia_entrega: diaEntrega || null,
      ventana_horaria: ventanaHoraria,
      semana_mes: semanaMes || null,
      dia_cobro_mes: diaCobroMes ? Number(diaCobroMes) : null,
      modelo_cobro: modeloCobro || 'prepaid_mensiversario',
      portero_referido_id: porteroReferidoId || null,
      notas: notas?.trim() || null,
      estado: 'activa',
      fecha_inicio: new Date().toISOString().slice(0, 10),
    })
    .select(`
      id, alias, barrio, direccion, dia_entrega, ventana_horaria,
      tipo_vivienda, lat, lng, estado, kit_actual,
      titular:profiles!clientes_titular_fkey(id, nombre, phone),
      beneficiario:profiles!clientes_beneficiario_fkey(id, nombre, phone),
      kit:kits!clientes_kit_actual_fkey(nombre, tier)
    `)
    .single()
  if (error) throw error
  return data
}

/**
 * Crea una suscripción FUERA DE ZONA (excepción manual del admin).
 * No exige barrio_id, no hace ruteo automático. Alguien (socio) se hace cargo de la entrega.
 */
export async function crearSuscripcionFueraZona({
  titularId, beneficiarioId = null, alias = null,
  barrioNombre, direccion, apto = null, tipoVivienda = null,
  kitCodigo = null, diaEntrega = null, ventanaHoraria = 'manana',
  semanaMes = null, diaCobroMes = null,
  modeloCobro = 'prepaid_mensiversario',
  entregaResponsable,  // 'gimena' | 'nico' | 'roberto' | 'otro'
  notas = null,
}) {
  if (!titularId) throw new Error('Falta titular')
  if (!direccion?.trim()) throw new Error('Falta dirección')
  if (!entregaResponsable) throw new Error('Falta indicar quién se hace cargo de la entrega')

  const { data, error } = await supabase
    .from('clientes')
    .insert({
      titular_id: titularId,
      beneficiario_id: beneficiarioId || null,
      alias: alias?.trim() || null,
      barrio_id: null,                                       // sin FK — fuera de cobertura
      barrio: (barrioNombre || 'Fuera de zona').trim(),
      direccion: direccion.trim(),
      apto: apto?.trim() || null,
      tipo_vivienda: tipoVivienda || null,
      lat: null, lng: null, geocoded_at: null,               // sin geocoding
      kit_actual: kitCodigo || null,
      dia_entrega: diaEntrega || null,
      ventana_horaria: ventanaHoraria,
      semana_mes: semanaMes || null,
      dia_cobro_mes: diaCobroMes ? Number(diaCobroMes) : null,
      modelo_cobro: modeloCobro || 'prepaid_mensiversario',
      portero_referido_id: null,
      notas: notas?.trim() || null,
      estado: 'activa',
      fecha_inicio: new Date().toISOString().slice(0, 10),
      fuera_zona: true,
      entrega_responsable: entregaResponsable,
    })
    .select(`
      id, alias, barrio, direccion, dia_entrega, ventana_horaria,
      tipo_vivienda, estado, kit_actual, fuera_zona, entrega_responsable,
      titular:profiles!clientes_titular_fkey(id, nombre, phone),
      beneficiario:profiles!clientes_beneficiario_fkey(id, nombre, phone),
      kit:kits!clientes_kit_actual_fkey(nombre, tier)
    `)
    .single()
  if (error) throw error
  return data
}

/** Pausa o reactiva una suscripción. */
export async function actualizarEstadoSuscripcion(id, estado, pausaHasta = null) {
  const { error } = await supabase
    .from('clientes')
    .update({ estado, pausa_hasta: pausaHasta })
    .eq('id', id)
  if (error) throw error
}

/**
 * Edita campos de la suscripción. Campos soportados:
 * alias, barrio_id, barrio (cache), direccion, apto, tipo_vivienda,
 * kit_actual, dia_entrega, ventana_horaria, semana_mes,
 * portero_referido_id, notas, lat, lng (manuales).
 *
 * Si pasás coordsManual, usa esas coords. Si no y cambia la dirección,
 * intenta regeocodificar. Si no, mantiene las viejas.
 */
export async function actualizarSuscripcion(id, campos, { coordsManual = null } = {}) {
  const updates = { ...campos }

  // Si vienen coords manuales (pin del mapa), las usamos
  if (coordsManual?.lat) {
    updates.lat = coordsManual.lat
    updates.lng = coordsManual.lng
    updates.geocoded_at = new Date().toISOString()
  } else if (campos.direccion !== undefined || campos.barrio !== undefined) {
    // Intentar regeocodificar si cambió la dirección/barrio
    const cli = await supabase.from('clientes').select('barrio,direccion').eq('id', id).maybeSingle()
    const dir = campos.direccion ?? cli.data?.direccion
    const barrio = campos.barrio ?? cli.data?.barrio
    if (dir && barrio) {
      const geo = await geocodificar(`${dir}, ${barrio}`)
      if (geo) {
        updates.lat = geo.lat
        updates.lng = geo.lng
        updates.geocoded_at = new Date().toISOString()
      }
    }
  }

  const { error } = await supabase.from('clientes').update(updates).eq('id', id)
  if (error) throw error
}

/** Actualiza datos de una persona (profile): nombre, phone, email, documento, permisos. */
export async function actualizarPersona(id, campos) {
  const updates = {}
  if (campos.nombre !== undefined) updates.nombre = campos.nombre?.trim() || null
  if (campos.phone !== undefined) updates.phone = campos.phone?.trim() || null
  if (campos.email !== undefined) updates.email = campos.email?.trim()?.toLowerCase() || null
  if (campos.documento !== undefined) updates.documento = campos.documento?.trim() || null
  if (campos.documento_tipo !== undefined) updates.documento_tipo = campos.documento_tipo || null
  if (campos.transferencia_habilitada !== undefined) updates.transferencia_habilitada = !!campos.transferencia_habilitada
  if (campos.transferencia_habilitada_at !== undefined) updates.transferencia_habilitada_at = campos.transferencia_habilitada_at
  const { error } = await supabase.from('profiles').update(updates).eq('id', id)
  if (error) {
    const msg = String(error.message || '')
    if (msg.includes('profiles_email_unq')) throw new Error('Ya existe alguien con ese email.')
    if (msg.includes('profiles_documento_unq')) throw new Error('Ya existe alguien con ese documento.')
    throw error
  }
}

/* ─────────────────────── KITS / PORTEROS lookup ─────────────────────── */

/** Lista de barrios activos (cobertura). Incluye sus días disponibles. */
export async function getBarrios(soloActivos = true) {
  let q = supabase.from('barrios').select('id, nombre, dias, activo, notas').order('nombre')
  if (soloActivos) q = q.eq('activo', true)
  const { data, error } = await q
  if (error) { console.error('getBarrios:', error); return [] }
  return data || []
}

export async function getKits() {
  const { data, error } = await supabase
    .from('kits')
    .select('codigo, nombre, tier, precio_susc')
    .order('codigo')
  if (error) return []
  return data || []
}

export async function getPorterosActivos() {
  const { data, error } = await supabase
    .from('porteros')
    .select(`
      id, slug, lugar,
      profile:profiles!porteros_id_fkey(nombre)
    `)
    .eq('activo', true)
    .order('lugar')
  if (error) return []
  return data || []
}
